import * as React from "react";
import {
  Paper,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  TableContainer,
  Chip,
  IconButton,
  Collapse,
  Box,
  Stack,
  LinearProgress,
  Divider,
  Grid
} from "@mui/material";
import {
  ExpandMore,
  ExpandLess,
  Insights
} from "@mui/icons-material";
import { EquipmentsRecord } from "../../interfaces/Equipment.interface";
import { EquipmentNameListLabels } from "../../common/consts/EquipmentNameList.const";
import { EquipmentUnitListLabels } from "../../common/consts/RquipmentUnit.const";
import { WastesChoisesLabels } from "../../common/consts/wasteList.const";
import { formatPlanSummary } from "../../utils/formatPlanSummary";
import { EquipmentIconWithPreview } from "./EquipmentVisual";

type Stats = NonNullable<EquipmentsRecord["stats"]>;

const StatLine: React.FC<{ label: string; value: number; suffix?: string }> = ({
  label,
  value,
  suffix = "",
}) => (
  <Stack direction="row" justifyContent="space-between" spacing={2}>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={600}>
      {Number.isFinite(value) ? value.toFixed(2) : "0.00"}
      {suffix}
    </Typography>
  </Stack>
);

const ChipsRow: React.FC<{ stats?: Stats }> = ({ stats }) => {
  const avgs = stats?.avgs || ({} as any);
  const items: Array<{
    key: "_0" | "_25" | "_50" | "_75" | "_100" | "_125";
    color: any;
    label: string;
  }> = [
    { key: "_0", color: "default", label: "0%" },
    { key: "_25", color: "error", label: "25%" },
    { key: "_50", color: "warning", label: "50%" },
    { key: "_75", color: "info", label: "75%" },
    { key: "_100", color: "success", label: "100%" },
    { key: "_125", color: "error", label: "125%" },
  ];
  return (
    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
      {items.map((i) => (
        <Chip
          key={i.key}
          size="small"
          color={i.color as any}
          variant="outlined"
          label={`${i.label}: ${Number(avgs?.[i.key] || 0).toFixed(2)}`}
        />
      ))}
    </Stack>
  );
};

const EquipmentRow: React.FC<{ eq: EquipmentsRecord }> = ({ eq }) => {
  const [open, setOpen] = React.useState(false);
  const stats = eq.stats as Stats | undefined;

  const totalDecl = stats?.totalDeclarations ?? 0;
  const eqFullAvg = Number(stats?.avgs?.equivalentFullUnits ?? 0);
  const quantity = Number(eq.quantity || 0);
  const fillRateAvgPct = quantity > 0 ? (eqFullAvg / quantity) * 100 : 0;

  const labelText =
    (EquipmentNameListLabels[eq.name] || eq.name || "Équipement") +
    (eq.size ? ` ${eq.size}` : "");

  return (
    <>
      <TableRow hover>
        <TableCell>
          <EquipmentIconWithPreview
            name={eq.name}
            size={eq.size}
            labelText={labelText}
          />
        </TableCell>

        <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
          <Typography variant="body2">
            {EquipmentNameListLabels[eq.name] || eq.name || "N/A"} {eq.size}{" "}
            {EquipmentUnitListLabels[eq.unit] || eq.unit || "N/A"}
          </Typography>
        </TableCell>

        <TableCell align="center">
          {WastesChoisesLabels[eq.waste] || eq.waste}
        </TableCell>

        <TableCell align="center">
          {formatPlanSummary(eq.collectionPlan ?? {})}
        </TableCell>

        <TableCell align="center">{eq.quantity}</TableCell>

        <TableCell align="center" width={64}>
          <IconButton
            onClick={() => setOpen((o) => !o)}
            size="small"
            aria-label="Stats"
          >
            {open ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell colSpan={6} sx={{ p: 0, border: 0 }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box
              sx={{
                bgcolor: "#fff",
                px: 2,
                py: 2,
                borderTop: "1px solid",
                borderColor: "divider",
              }}
            >
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ mb: 1 }}
              >
                <Insights fontSize="small" />
                <Typography variant="subtitle2">
                  Statistiques moyennes par déclaration
                </Typography>
              </Stack>

              {!stats ? (
                <Typography variant="body2" color="text.secondary">
                  Aucune déclaration pour le moment.
                </Typography>
              ) : (
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12 }}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <StatLine
                        label="Déclarations comptabilisées"
                        value={Number(totalDecl || 0)}
                      />
                      <Divider sx={{ my: 1 }} />
                      <StatLine
                        label="Équivalent plein (moy.)"
                        value={eqFullAvg}
                      />
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          Taux moyen vs quantité installée
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={Math.max(0, Math.min(fillRateAvgPct, 250))}
                          sx={{
                            height: 8,
                            borderRadius: 4,
                            mt: 0.5,
                            "& .MuiLinearProgress-bar": {
                              transition: "width .3s",
                            },
                          }}
                        />
                        <Typography variant="caption">
                          {fillRateAvgPct.toFixed(1)}%
                          {quantity > 0 ? ` (quantité: ${quantity})` : ""}
                        </Typography>
                      </Box>
                    </Paper>
                  </Grid>

                  <Grid size={{ xs: 12 }}>
                    <Paper variant="outlined" sx={{ p: 2, height: "100%" }}>
                      <Typography variant="caption" color="text.secondary">
                        Répartition moyenne des niveaux (nb moyen de contenants
                        par déclaration)
                      </Typography>
                      <Box sx={{ mt: 1 }}>
                        <ChipsRow stats={stats} />
                      </Box>
                    </Paper>
                  </Grid>
                </Grid>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

export const EquipmentList: React.FC<{ equipments: EquipmentsRecord[] }> = ({
  equipments,
}) => {
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);

  const handleChangePage = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => setPage(newPage);

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginated = React.useMemo(
    () =>
      equipments.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [equipments, page, rowsPerPage]
  );

  return (
    <Paper sx={{ p: 2, mt: 2 }}>
      <Typography variant="h6" gutterBottom>
        Vos Équipements
      </Typography>

      <TableContainer sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell align="center">Nom</TableCell>
              <TableCell align="center">Déchet</TableCell>
              <TableCell align="center">Plan collecte</TableCell>
              <TableCell align="center">Quantité</TableCell>
              <TableCell align="center" width={64}>
                Stats
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginated.map((eq) => (
              <EquipmentRow key={eq._id} eq={eq} />
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={equipments.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25]}
        />
      </TableContainer>
    </Paper>
  );
};
